'use client'

import * as React from 'react'
import Link from 'next/link'
import { ChevronRight, Home } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface PageHeaderProps {
  title: string
  description?: React.ReactNode
  breadcrumbs?: BreadcrumbItem[]
  actions?: React.ReactNode
  /** Sits on the title's line - a status badge, a priority. */
  titleAddon?: React.ReactNode
  className?: string
}

/**
 * The top of every Helm page.
 *
 * The page shell already pads the content area, so this adds none of its own:
 * it used to draw a bordered band with its own padding, which left these
 * pages indented twice as far as the rest. The title is set in the DS display
 * face (the base h1 rule), the description wraps instead of truncating, and
 * actions drop below the title on a phone.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  titleAddon,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-2', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
        >
          <Link href="/" aria-label="Dashboard" className="flex items-center hover:text-foreground">
            <Home className="h-3.5 w-3.5" />
          </Link>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight aria-hidden className="h-3.5 w-3.5" />
              {crumb.href ? (
                <Link href={crumb.href} className="hover:text-foreground">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="font-medium text-foreground">
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold break-words md:text-3xl">{title}</h1>
            {titleAddon}
          </div>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground md:text-base">{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
      </div>
    </header>
  )
}
